/**
 * @typedef {Object} NormalizedStyle
 * @property {string[]} styles - All styles to apply to the range {`offset`, `offset + length - 1`}
 * @property {number} offset - The start offset of these `styles`
 * @property {number} length - The number of character code points these `styles` applies to.
 */

/**
 * Turns styles into a matrix of offsets and styles for each offset.
 *
 * @param {ArticleFormatStyle[]} styles The styles to create a matrix from
 * @return {Array.<string[]>} The two-dimensional style matrix
 * @private
 */
'use strict';

Object.defineProperty(exports, '__esModule', {
    value: true
});

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function _objectWithoutProperties(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function createStyleMatrix(styles) {
    return styles.reduce(function (matrix, style) {
        var isSameStyleType = function isSameStyleType(existingStyle) {
            return existingStyle.type === style.type;
        };
        for (var ix = style.offset; ix < style.offset + style.length; ix++) {
            matrix[ix] = matrix[ix] || [];
            if (!matrix[ix].some(isSameStyleType)) {
                matrix[ix].push(style);
            }
        }
        return matrix;
    }, []);
}

/**
 * Given an array of normalized styles, this function creates a new array where each index between 0
 * and `length` has styles
 *
 * @param {NormalizedStyle[]} normalized The explicit styles of the text
 * @param {number} length The total length of the text the styles in `normalized` applies to
 * @return {NormalizedStyle[]} The normalized styles with additional styles added to cover the gaps in `normalized`
 * @private
 */
function fillGaps(normalized, length) {
    var firstStyle = normalized[0];
    var lastStyle = normalized[normalized.length - 1];

    normalized.forEach(function (style, ix) {
        var prevStyle = normalized[ix - 1];
        if (prevStyle) {
            var styleEnd = prevStyle.offset + prevStyle.length;
            if (styleEnd < style.offset) {
                normalized.push({
                    styles: [],
                    offset: styleEnd,
                    length: style.offset - styleEnd
                });
            }
        }
    });

    if (firstStyle && firstStyle.offset > 0) {
        normalized.push({
            styles: [],
            offset: 0,
            length: firstStyle.offset
        });
    }

    if (lastStyle) {
        var styleEnd = lastStyle.offset + lastStyle.length;
        if (styleEnd < length) {
            normalized.push({
                styles: [],
                offset: styleEnd,
                length: length - styleEnd
            });
        }
    }

    return normalized;
}

/**
 * Sorts normalized styles by their offset
 * @param {NormalizedStyle[]} styles The styles to sort
 * @return {NormalizedStyle[]} `styles` sorted by offset in ascending order
 * @private
 */
function sortByOffset(styles) {
    styles.sort(function (styleA, styleB) {
        return styleA.offset - styleB.offset;
    });
    return styles;
}

/**
 * Turns an array of styles in {@link ArticleFormatStyle} into an array of styles in {@link NormalizedStyle}
 *
 * @param {ArticleFormatStyle[]} styles The styles to convert to normalized form
 * @param {number} length The length of the text the `styles` applies to
 * @return {NormalizedStyle[]} `styles` converted into an array of {@link NormalizedStyle}
 */
function normalizeStyles(styles, length) {
    var styleMatrix = createStyleMatrix(styles);
    var normalizedStyles = [];

    var currentStyle = undefined;
    var lastSeenStyleKey = undefined;
    var prevIx = undefined;
    styleMatrix.forEach(function (offsetStyles, ix) {
        var styleKey = offsetStyles.map(function (s) {
            return s.type;
        }).join(',');
        if (lastSeenStyleKey !== styleKey || ix - prevIx !== 1) {
            currentStyle = {
                offset: ix,
                length: 0,
                styles: offsetStyles.map(function (_ref) {
                    var type = _ref.type;
                    var length = _ref.length;
                    var offset = _ref.offset;

                    var rest = _objectWithoutProperties(_ref, ['type', 'length', 'offset']);

                    return _extends({}, rest, {
                        type: type
                    });
                })
            };
            normalizedStyles.push(currentStyle);
        }
        lastSeenStyleKey = styleKey;
        currentStyle.length++;
        prevIx = ix;
    });
    normalizedStyles = fillGaps(normalizedStyles, length);
    return sortByOffset(normalizedStyles);
}

exports['default'] = normalizeStyles;
module.exports = exports['default'];
// eslint-disable-line no-unused-vars, no-shadow
// length and offset extracted to exclude it from `rest` (which contains custom metadata eg uri)


/*****************
 ** WEBPACK FOOTER
 ** ./~/@aftonbladet/stylizr/lib/normalize-styles.js
 ** module id = 453
 ** module chunks = 1
 **/